import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { proposalsApi } from '../../services/api';
import type { Proposal, ProposalItem } from '../../types';
import ItemSelectionModal from '../../components/ItemSelectionModal';
import { 
  ArrowLeftIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  'novo_lead': { label: 'Novo Lead', color: 'bg-gray-100 text-gray-800', icon: ClockIcon },
  'qualificacao': { label: 'Qualificação', color: 'bg-blue-100 text-blue-800', icon: ClockIcon },
  'em_elaboracao': { label: 'Em Elaboração', color: 'bg-yellow-100 text-yellow-800', icon: PencilIcon },
  'aguardando_aprovacao': { label: 'Aguard. Aprovação', color: 'bg-orange-100 text-orange-800', icon: ClockIcon },
  'enviada_cliente': { label: 'Enviada Cliente', color: 'bg-indigo-100 text-indigo-800', icon: DocumentTextIcon },
  'em_negociacao': { label: 'Em Negociação', color: 'bg-purple-100 text-purple-800', icon: ClockIcon },
  'em_analise_cliente': { label: 'Análise Cliente', color: 'bg-cyan-100 text-cyan-800', icon: ClockIcon },
  'aprovada': { label: 'Aprovada', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
  'em_geracao_contrato': { label: 'Ger. Contrato', color: 'bg-emerald-100 text-emerald-800', icon: DocumentTextIcon },
  'aguardando_assinatura': { label: 'Aguard. Assinatura', color: 'bg-teal-100 text-teal-800', icon: ClockIcon },
  'assinado': { label: 'Assinado', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
  'em_execucao': { label: 'Em Execução', color: 'bg-green-100 text-green-800', icon: CheckCircleIcon },
  'cancelada': { label: 'Cancelada', color: 'bg-red-100 text-red-800', icon: XCircleIcon },
  'perdida': { label: 'Perdida', color: 'bg-red-100 text-red-800', icon: XCircleIcon },
  'rejeitada_assinatura': { label: 'Rejeitada', color: 'bg-red-100 text-red-800', icon: XCircleIcon },
};

const ProposalDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProposalItem, setSelectedProposalItem] = useState<ProposalItem | null>(null);
  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);

  useEffect(() => {
    console.log('🔍 ProposalDetail useEffect - ID:', id, typeof id);
    if (id && id !== 'undefined' && id.trim() !== '') {
      // Mais tolerante: pelo menos tem formato UUID básico
      if (id.length >= 30 && id.includes('-')) {
        console.log('✅ ProposalDetail: Loading proposal with ID:', id);
        loadProposal();
      } else {
        console.log('ℹ️ ProposalDetail: ID formato inválido:', id, 'length:', id.length);
        setLoading(false);
        setError('ID da proposta inválido - ' + id);
      }
    } else {
      console.log('ℹ️ ProposalDetail: ID não fornecido:', id);
      setLoading(false);
      setError('ID da proposta inválido');
    }
  }, [id]);

  const loadProposal = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('🔄 ProposalDetail: Fetching proposal:', id);
      const data = await proposalsApi.getProposal(id!);
      console.log('✅ ProposalDetail: Proposal loaded:', data);
      setProposal(data);
    } catch (err: any) {
      console.error('❌ ProposalDetail: Erro ao carregar proposta:', err);
      setError(err.response?.data?.detail || 'Erro ao carregar proposta');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatDocument = (document: string, personType: string) => {
    if (!document) return '';
    if (personType === 'PF') {
      return document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else {
      return document.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  };

  const handleOpenItemSelection = (item: ProposalItem) => {
    setSelectedProposalItem(item);
    setIsSelectionModalOpen(true);
  };

  const handleCloseItemSelection = () => {
    setIsSelectionModalOpen(false);
    setSelectedProposalItem(null);
  };

  const handleSelectionComplete = () => {
    // Recarregar a proposta para atualizar o status das seleções
    loadProposal();
  };

  const handleViewPDF = async () => {
    if (!proposal) return;
    
    try {
      const blob = await proposalsApi.generatePdf(proposal.id);
      const url = window.URL.createObjectURL(blob);
      
      // Open PDF in new window
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        // If popup blocked, create download link
        const a = document.createElement('a');
        a.href = url;
        a.download = `proposta_${proposal.number}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      
      // Clean up the URL object
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      setError('Erro ao gerar PDF da proposta');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400 mb-1" />
        <h3 className="text-xs font-medium text-secondary-900 mb-2">Erro ao carregar proposta</h3>
        <p className="text-secondary-600 mb-1">{error}</p>
        <button onClick={() => navigate('/proposals')} className="btn-primary whitespace-nowrap">
          Voltar para Propostas
        </button>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="text-center py-12">
        <h3 className="text-xs font-medium text-secondary-900 mb-2">Proposta não encontrada</h3>
        <button onClick={() => navigate('/proposals')} className="btn-primary whitespace-nowrap">
          Voltar para Propostas
        </button>
      </div>
    );
  }

  const statusInfo = statusConfig[proposal.status] || statusConfig['em_elaboracao'];
  const StatusIcon = statusInfo.icon;

  return (
    <div className="space-y-1">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/proposals')}
            className="btn-secondary flex items-center"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1.5" />
            Voltar
          </button>
          
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-xs font-bold text-secondary-900">
                Proposta {proposal.number}
              </h1>
              <span className="text-secondary-500">v{proposal.version}</span>
              <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.color}`}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusInfo.label}
              </span>
            </div>
            <p className="text-secondary-600">
              Criada em {formatDateTime(proposal.created_at)} por {proposal.responsible_name}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Link
            to={`/proposals/negotiate?id=${proposal.id}`}
            className="btn-secondary flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4 mr-1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
            </svg>
            Negociar
          </Link>
          <button className="btn-secondary flex items-center">
            <DocumentDuplicateIcon className="h-4 w-4 mr-1.5" />
            Duplicar
          </button>
          <button 
            onClick={handleViewPDF}
            className="btn-primary flex items-center"
          >
            <EyeIcon className="h-4 w-4 mr-1.5" />
            Visualizar PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-1">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-1">
          {/* Cliente Information */}
          <div className="bg-white border border-secondary-200 rounded-lg p-2">
            <h2 className="flex items-center text-xs font-medium text-secondary-900 mb-1">
              {proposal.client.person_type === 'PF' ? (
                <UserIcon className="h-4 w-4 mr-1.5" />
              ) : (
                <BuildingOfficeIcon className="h-4 w-4 mr-1.5" />
              )}
              Informações do Cliente
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
              <div>
                <label className="block text-xs font-medium text-secondary-700 mb-1">Nome</label>
                <p className="text-xs text-secondary-900">{proposal.client.name}</p>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-secondary-700 mb-1">Documento</label>
                <p className="text-xs text-secondary-900">
                  {formatDocument(proposal.client.cpf_cnpj, proposal.client.person_type)}
                </p>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-secondary-700 mb-1">Email</label>
                <p className="text-xs text-secondary-900">{proposal.client.email}</p>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-secondary-700 mb-1">Telefone</label>
                <p className="text-xs text-secondary-900">{proposal.client.phone}</p>
              </div>

              {proposal.client.city && (
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-secondary-700 mb-1">Endereço</label>
                  <p className="text-xs text-secondary-900">
                    {proposal.client.full_address || `${proposal.client.city}, ${proposal.client.state}`}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Período e Localização */}
          <div className="bg-white border border-secondary-200 rounded-lg p-2">
            <h2 className="flex items-center text-xs font-medium text-secondary-900 mb-1">
              <CalendarIcon className="h-4 w-4 mr-1.5" />
              Período e Localização
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
              <div>
                <h3 className="text-xs font-medium text-secondary-700 mb-3">Período de Locação</h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Início:</span>
                    <span className="text-secondary-900">{formatDate(proposal.start_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Término:</span>
                    <span className="text-secondary-900">{formatDate(proposal.end_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Duração:</span>
                    <span className="text-secondary-900">{proposal.rental_period_days} dias</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="flex items-center text-xs font-medium text-secondary-700 mb-3">
                  <MapPinIcon className="h-4 w-4 mr-1" />
                  Local de Entrega
                </h3>
                <div className="space-y-1 text-xs">
                  <p className="text-secondary-900">{proposal.delivery_address}</p>
                  <p className="text-secondary-600">
                    {proposal.delivery_city}, {proposal.delivery_state}
                  </p>
                  <p className="text-secondary-600">CEP: {proposal.delivery_zip_code}</p>
                  <p className="text-secondary-600">Distância: {proposal.distance_km} km</p>
                </div>
              </div>
            </div>
          </div>

          {/* Itens da Proposta */}
          <div className="bg-white border border-secondary-200 rounded-lg p-2">
            <h2 className="flex items-center text-xs font-medium text-secondary-900 mb-1">
              <DocumentTextIcon className="h-4 w-4 mr-1.5" />
              Itens da Proposta ({proposal.items?.length || 0})
            </h2>
            
            <div className="space-y-1">
              {proposal.items?.map((item, index) => (
                <div key={index} className="border border-secondary-100 rounded-lg p-1">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium text-secondary-900">{item.service_name}</h3>
                      {item.service_category && (
                        <p className="text-xs text-secondary-500">{item.service_category}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-secondary-900">
                        {formatCurrency(item.total_price)}
                      </p>
                      <p className="text-xs text-secondary-500">
                        {item.quantity} x {formatCurrency(item.unit_price)}
                      </p>
                    </div>
                  </div>

                  {/* Item Selection Interface (Hybrid C+B System) */}
                  {item.service && item.service.track_individual_items && 
                   (proposal.status === 'aprovada' || proposal.status === 'assinado' || proposal.status === 'em_execucao') && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <Squares2X2Icon className="h-4 w-4 text-blue-600 mr-1" />
                          <span className="text-xs font-medium text-blue-800">
                            Seleção Individual de Itens
                          </span>
                        </div>
                        <button
                          onClick={() => handleOpenItemSelection(item)}
                          className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700 transition-colors"
                        >
                          Selecionar Itens
                        </button>
                      </div>
                      
                      {/* Selection Status */}
                      <div className="flex items-center justify-between text-xs">
                        <div className="text-blue-600">
                          <strong>Status:</strong> {item.selected_items_count || 0}/{item.quantity} selecionados
                        </div>
                        <div className={`px-2 py-1 rounded ${
                          (item.selected_items_count || 0) === item.quantity 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {(item.selected_items_count || 0) === item.quantity ? '✅ Completo' : '⏳ Pendente'}
                        </div>
                      </div>
                      
                      {/* Selected Items Summary */}
                      {item.selected_items && item.selected_items.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs text-blue-700 mb-1">Itens Selecionados:</div>
                          <div className="flex flex-wrap gap-1">
                            {item.selected_items.slice(0, 5).map((selection) => (
                              <span
                                key={selection.id}
                                className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-green-100 text-green-800"
                                title={`${selection.service_item_code} - ${selection.selected_automatically ? 'Auto' : 'Manual'}`}
                              >
                                {selection.service_item_code}
                                {selection.selected_automatically && ' 🤖'}
                              </span>
                            ))}
                            {item.selected_items.length > 5 && (
                              <span className="text-xs text-blue-600">
                                +{item.selected_items.length - 5} mais
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {item.custom_description && (
                    <p className="text-xs text-secondary-600 mt-2">
                      <strong>Descrição:</strong> {item.custom_description}
                    </p>
                  )}
                  
                  {item.notes && (
                    <p className="text-xs text-secondary-500 mt-1">
                      <strong>Observações:</strong> {item.notes}
                    </p>
                  )}
                </div>
              )) || (
                <p className="text-center text-secondary-500 py-4">Nenhum item encontrado</p>
              )}
            </div>
          </div>

          {/* Observações */}
          {(proposal.internal_notes || proposal.client_notes) && (
            <div className="bg-white border border-secondary-200 rounded-lg p-2">
              <h2 className="text-xs font-medium text-secondary-900 mb-1">Observações</h2>
              
              {proposal.internal_notes && (
                <div className="mb-1">
                  <h3 className="text-xs font-medium text-secondary-700 mb-2">Observações Internas</h3>
                  <p className="text-xs text-secondary-600 bg-secondary-50 p-3 rounded">
                    {proposal.internal_notes}
                  </p>
                </div>
              )}
              
              {proposal.client_notes && (
                <div>
                  <h3 className="text-xs font-medium text-secondary-700 mb-2">Observações para o Cliente</h3>
                  <p className="text-xs text-secondary-600 bg-blue-50 p-3 rounded">
                    {proposal.client_notes}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-1">
          {/* Resumo Financeiro */}
          <div className="bg-white border border-secondary-200 rounded-lg p-2">
            <h2 className="flex items-center text-xs font-medium text-secondary-900 mb-1">
              <CurrencyDollarIcon className="h-4 w-4 mr-1.5" />
              Resumo Financeiro
            </h2>
            
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-secondary-600">Subtotal:</span>
                <span className="text-secondary-900">{formatCurrency(proposal.subtotal)}</span>
              </div>
              
              {proposal.discount_percentage > 0 && (
                <div className="flex justify-between">
                  <span className="text-secondary-600">
                    Desconto ({proposal.discount_percentage}%):
                  </span>
                  <span className="text-red-600">
                    -{formatCurrency(proposal.discount_amount)}
                  </span>
                </div>
              )}
              
              {proposal.transport_surcharge_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-secondary-600">Taxa de transporte:</span>
                  <span className="text-secondary-900">
                    +{formatCurrency(proposal.transport_surcharge_amount)}
                  </span>
                </div>
              )}
              
              <div className="border-t border-secondary-200 pt-3">
                <div className="flex justify-between">
                  <span className="font-medium text-secondary-900">Total:</span>
                  <span className="text-xs font-bold text-secondary-900">
                    {formatCurrency(proposal.total)}
                  </span>
                </div>
              </div>
            </div>

            {/* Business Rules */}
            {proposal.business_rules_applied && (
              <div className="mt-4 pt-4 border-t border-secondary-200">
                <h3 className="text-xs font-medium text-secondary-700 mb-2">Regras Aplicadas</h3>
                <div className="text-xs text-secondary-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Transporte incluído:</span>
                    <span>{proposal.transport_included ? 'Sim' : 'Não'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Mínimo de cabines:</span>
                    <span>{proposal.business_rules_applied.minimum_cabins}</span>
                  </div>
                  {proposal.transport_surcharge_percentage > 0 && (
                    <div className="flex justify-between">
                      <span>Taxa de distância:</span>
                      <span>{proposal.transport_surcharge_percentage}%</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Status and Actions */}
          <div className="bg-white border border-secondary-200 rounded-lg p-2">
            <h2 className="text-xs font-medium text-secondary-900 mb-1">Status e Ações</h2>
            
            <div className="space-y-1">
              <div>
                <label className="block text-xs font-medium text-secondary-700 mb-1">Status Atual</label>
                <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                  <StatusIcon className="h-4 w-4 mr-1.5" />
                  {statusInfo.label}
                </span>
              </div>

              {proposal.requires_approval && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <div className="flex items-center text-amber-800">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-1.5" />
                    <span className="text-xs font-medium">Aprovação Necessária</span>
                  </div>
                  <p className="text-xs text-amber-700 mt-1">
                    Esta proposta excede os limites de desconto padrão.
                  </p>
                </div>
              )}

              {proposal.sent_to_client_at && (
                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-1">
                    Enviada ao Cliente
                  </label>
                  <p className="text-xs text-secondary-900">
                    {formatDateTime(proposal.sent_to_client_at)}
                  </p>
                </div>
              )}

              {proposal.client_response_deadline && (
                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-1">
                    Prazo de Resposta
                  </label>
                  <p className="text-xs text-secondary-900">
                    {formatDate(proposal.client_response_deadline)}
                  </p>
                </div>
              )}

              {proposal.approved_by && (
                <div>
                  <label className="block text-xs font-medium text-secondary-700 mb-1">
                    Aprovada por
                  </label>
                  <p className="text-xs text-secondary-900">
                    {proposal.approved_by.first_name} {proposal.approved_by.last_name}
                  </p>
                  {proposal.approved_at && (
                    <p className="text-xs text-secondary-500">
                      em {formatDateTime(proposal.approved_at)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-2">
            <h3 className="text-xs font-medium text-primary-900 mb-3">Resumo</h3>
            <div className="grid grid-cols-2 gap-1 text-center">
              <div>
                <div className="text-xs font-bold text-primary-900">{proposal.items_count || 0}</div>
                <div className="text-xs text-primary-700">Itens</div>
              </div>
              <div>
                <div className="text-xs font-bold text-primary-900">{proposal.rental_period_days}</div>
                <div className="text-xs text-primary-700">Dias</div>
              </div>
              <div>
                <div className="text-xs font-bold text-primary-900">{proposal.distance_km}</div>
                <div className="text-xs text-primary-700">KM</div>
              </div>
              <div>
                <div className="text-xs font-bold text-primary-900">
                  {formatCurrency(proposal.total).replace('R$', '').trim()}
                </div>
                <div className="text-xs text-primary-700">Total</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Item Selection Modal */}
      {selectedProposalItem && (
        <ItemSelectionModal
          proposalItem={selectedProposalItem}
          isOpen={isSelectionModalOpen}
          onClose={handleCloseItemSelection}
          onSelectionComplete={handleSelectionComplete}
        />
      )}
    </div>
  );
};

export default ProposalDetail;